import React, { useState } from 'react';
import { TransactionError } from '@thirdweb-dev/react';
import { Box, Button, Divider, HStack, Text } from '@chakra-ui/react';
import { NetworkMismatchButton } from '@app/components/NetworkMismatchButton';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { Icon } from '@app/components/Icon';
import { LabeledValue } from '@app/components/LabeledValue';
import { formatNumber, toOptionalFixed } from '@app/helpers/format';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';
import { useStake, useXKimTokenApprove } from '@app/hooks/nft/useStake';
import { useUserXKimAllocationToPlugin } from '@app/hooks/nft/useUserXKimAllocationToPlugin';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@app/constants/queryKeys';
import { useGetMarketplaceRequirements } from '@app/hooks/nft/useGetMarketplaceRequirements';

export const Stake = () => {
  const {
    state,
    handleAmountChange,
    myXRyoBalance,
    allocate,
    allocating,
    loadingTokenBalance,
    resetStateValues
  } = useStake();

  const isMismatchedNetwork = useNetworkMismatch();

  const { data: currentAllocation } = useUserXKimAllocationToPlugin();
  const { data: requirements } = useGetMarketplaceRequirements();

  const {
    requiresApproval: requiresApproval,
    approve,
    isApproveInProgress: isApproveInProgress,
    getAllowance: getAllowance
  } = useXKimTokenApprove(state.amount);

  const queryClient = useQueryClient();

  const isBalanceExceeded =
    myXRyoBalance !== undefined && +myXRyoBalance < +state.amount;

  function renderInfoRow(label: string, value0: string, value1?: string) {
    return (
      <HStack justifyContent="space-between">
        <Text fontSize="16px" fontWeight={400} lineHeight="26px" color="gray.4">
          {label}
        </Text>
        <HStack>
          <Text
            fontSize="16px"
            fontWeight={500}
            lineHeight="26px"
            color="gray.4"
          >
            {value0}
          </Text>
          {value1 && (
            <>
              <Icon
                name="arrowRight"
                style={{ color: 'var(--chakra-colors-gray-4)' }}
              />
              <Text
                fontSize="16px"
                fontWeight={500}
                lineHeight="26px"
                color="gray.4"
              >
                {value1}
              </Text>
            </>
          )}
        </HStack>
      </HStack>
    );
  }

  function getSubmitLabel() {
    if (isBalanceExceeded) {
      return 'Insufficient balance';
    }

    if (!state.amount) {
      return 'Enter xKIM amount to stake';
    }

    return 'Stake';
  }

  function renderSubmitAction() {
    if (isMismatchedNetwork) {
      return <NetworkMismatchButton />;
    }

    if (requiresApproval && !isBalanceExceeded) {
      return (
        <SubmitButton
          mt={4}
          loadingText="Processing..."
          onClick={async () => {
            try {
              const res = await approve();

              await getAllowance();

              const txHash = res?.receipt?.transactionHash;

              if (txHash) {
                showSuccessToast(
                  `Successfully approved KIM`,
                  <ExternalLink txHash={txHash} />
                );
              }
            } catch (e) {
              if (e instanceof TransactionError) {
                showErrorToast(e.reason);
              } else {
                showErrorToast(`Error approving KIM`);
              }
            }
          }}
          isLoading={isApproveInProgress}
          label="Approve"
        />
      );
    }

    return (
      <SubmitButton
        disabled={isBalanceExceeded || !+state.amount}
        mt={4}
        onClick={async () => {
          try {
            const res = await allocate();

            if (res['reason'] !== undefined) {
              showErrorToast(res.reason);
            } else if (res instanceof Error) {
              showErrorToast(res.message);
            } else {
              resetStateValues();

              const txHash = res?.receipt?.transactionHash;

              await queryClient.invalidateQueries({
                queryKey: [QueryKeys.GET_USER_X_KIM_ALLOCATION_TU_PLUGIN]
              });

              await queryClient.invalidateQueries({
                queryKey: [QueryKeys.CHECK_NFT_MARKETPLACE_USER_ELIGIBILITY]
              });

              showSuccessToast(
                `Transaction successful`,
                <ExternalLink txHash={txHash} />
              );
            }
          } catch (e) {
            if (e instanceof TransactionError) {
              showErrorToast(e.reason);
            } else {
              showErrorToast(`Transaction failed`);
            }
          }
        }}
        isLoading={allocating}
        label={getSubmitLabel()}
      />
    );
  }

  return (
    <Box mt={4}>
      <HStack justifyContent="space-between">
        <LabeledValue
          label="My xKIM:"
          isLoading={loadingTokenBalance}
          value={!myXRyoBalance ? 0 : toOptionalFixed(+myXRyoBalance, 2)}
        />
      </HStack>
      <HStack
        justifyContent="space-between"
        mt={4}
        sx={{
          paddingRight: '14px',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'gray.5',
          background: 'black'
        }}
      >
        <DebouncedInput
          amount={state.amount}
          onChange={handleAmountChange}
          textAlign="left"
        />
        <Button
          variant="tertiary"
          onClick={() => {
            if (myXRyoBalance === undefined) {
              return;
            }

            handleAmountChange(myXRyoBalance.toString());
          }}
          sx={{
            color: 'orange',
            bg: 'gray.2',
            width: 'fit-content',
            fontSize: '14px',
            padding: '8px 14px'
          }}
        >
          MAX
        </Button>
      </HStack>
      <Divider sx={{ margin: '12px 0', background: 'gray.5', height: '1px' }} />
      {renderInfoRow(
        'Staked',
        `${
          currentAllocation ? toOptionalFixed(+currentAllocation, 2) : '-'
        } xKIM`
      )}
      {renderInfoRow(
        'Min staked amount to participate in listing',
        `${
          requirements
            ? formatNumber(+requirements.minAllocationForListing)
            : '-'
        } xKIM`
      )}
      {renderInfoRow(
        'Min staked amount to participate in Premium listing',
        `${
          requirements
            ? formatNumber(+requirements.minAllocationForPremiumListing)
            : '-'
        } xKIM`
      )}
      {renderSubmitAction()}
    </Box>
  );
};
